
import React, { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LayoutLoader } from "@/components/ui/layout-loader";
import { useToast } from "@/components/ui/use-toast";

// Import tab components
import SubsidyAllocationTab from "./subsidy-management/SubsidyAllocationTab";
import EligibleBeneficiariesTab from "./subsidy-management/EligibleBeneficiariesTab";
import UsageReportsTab from "./subsidy-management/UsageReportsTab";
import SubsidySettingsTab from "./subsidy-management/SubsidySettingsTab";
import { initializeSubsidyDatabase } from "@/lib/db/subsidyDb";

const SubsidyManagement = () => {
  const [activeTab, setActiveTab] = useState("subsidy-allocation");
  const [isLoading, setIsLoading] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const [initAttempts, setInitAttempts] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    const initialize = async () => {
      try {
        console.log(`Initializing subsidy database (attempt ${initAttempts + 1}) with timestamp-based versioning...`);
        setIsLoading(true);
        await initializeSubsidyDatabase();
        console.log("Subsidy database initialized successfully");
        setIsLoading(false);
        setInitError(null);
      } catch (error) {
        console.error("Failed to initialize subsidy database", error);
        setInitError(`Database initialization failed (attempt ${initAttempts + 1}). This may be due to a schema update. Please try refreshing the page.`);
        toast({
          variant: "destructive",
          title: "Database initialization failed",
          description: "There was an error setting up the subsidy database. Click retry or refresh the page.",
        });
        setIsLoading(false);
      }
    };
    
    initialize();
  }, [toast, initAttempts]);
  
  const handleRetry = () => {
    setInitError(null);
    setIsLoading(true);
    setInitAttempts(prev => prev + 1); // This will trigger the useEffect again
  };

  if (isLoading) {
    return <LayoutLoader message="Initializing subsidy database..." />;
  }

  if (initError) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <h2 className="text-xl font-semibold text-red-600 mb-4">{initError}</h2>
        <p className="text-muted-foreground mb-6">This could be due to browser storage issues or a temporary system error.</p>
        <div className="flex flex-col sm:flex-row gap-4">
          <button 
            onClick={handleRetry}
            className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/90"
          >
            Retry Initialization
          </button>
          <button 
            onClick={() => window.location.reload()}
            className="border border-input bg-background px-4 py-2 rounded hover:bg-accent"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Subsidy Management</h1>
        <p className="text-muted-foreground">
          Assign and track monthly/seasonal subsidies for eligible low-income customers and members.
        </p>
      </div>

      <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="w-full border-b justify-start">
          <TabsTrigger value="subsidy-allocation" className="px-8 py-2">Subsidy Allocation</TabsTrigger>
          <TabsTrigger value="eligible-beneficiaries" className="px-8 py-2">Eligible Beneficiaries</TabsTrigger>
          <TabsTrigger value="usage-reports" className="px-8 py-2">Usage Reports</TabsTrigger>
          <TabsTrigger value="subsidy-settings" className="px-8 py-2">Subsidy Settings</TabsTrigger>
        </TabsList>
        
        <TabsContent value="subsidy-allocation" className="space-y-4">
          <SubsidyAllocationTab />
        </TabsContent>
        
        <TabsContent value="eligible-beneficiaries" className="space-y-4">
          <EligibleBeneficiariesTab />
        </TabsContent>
        
        <TabsContent value="usage-reports" className="space-y-4">
          <UsageReportsTab />
        </TabsContent>
        
        <TabsContent value="subsidy-settings" className="space-y-4">
          <SubsidySettingsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SubsidyManagement;
